/* THE UNITS TABLE, FETCHED ONCE PER PAGE LOAD.
 *
 * ⚠️ ONE FETCH, SHARED — the same discipline `sharedCanvaStatus()` applies to
 * `/api/canva/status`. Six pickers asking that question separately is six
 * round-trips that can disagree with each other; a worklist, a manager view and
 * a drawer each fetching the units table would be three answers to "what is a
 * football championship worth" on one screen. The module-level promise is the
 * share, and it is keyed to nothing because the table is not per-user.
 *
 * The endpoint already answers `cache-control: private, max-age=300,
 * must-revalidate`, so the browser handles staleness; this only stops the same
 * page asking three times in one render pass.
 */
import { useEffect, useState } from 'react'
import { api } from '../../../lib/api.js'
import { readUnitsTable } from './unitsTable.js'

let shared = null

/** Drop the shared promise — for a retry after a failure. */
export function resetUnitsTable() { shared = null }

function load() {
  if (!shared) {
    shared = api.tamUnits()
      .then((payload) => readUnitsTable(payload, {
        measuredAtHeader: payload?.__headers?.['x-sr-units-measured-at'] || null,
        /* The price header is served on BOTH the envelope and the bare-array
         * shape, so it is the one source that survives the response shape
         * changing underneath us. The body still wins when it has an answer. */
        priceHeader: payload?.__headers?.['x-sr-price-per-unit-usd'] || null,
      }))
      /* ⚠️ A FAILED FETCH MUST NOT BE CACHED AS "no table forever". Clearing the
       * shared promise on rejection is what makes a retry possible at all —
       * otherwise the first network blip would leave every figure null for the
       * rest of the session and the worklist would quietly fall back to urgency
       * ordering with no way back. */
      .catch((err) => { shared = null; throw err })
  }
  return shared
}

/**
 * `{ table, status, error, reload }`.
 *
 * ⚠️ `table` IS null UNTIL IT LOADS, AND null IS NOT AN EMPTY TABLE. `rank()`
 * reads a null table as "no figures available" and reports
 * `ordering.mode: 'urgency'` rather than claiming a revenue ranking it could
 * not compute — a list that says it is ranked by money while every figure is
 * null is the same quiet lie as an alphabetical list presented as prioritised.
 */
export function useUnitsTable() {
  const [state, setState] = useState({ table: null, status: 'loading', error: null })

  useEffect(() => {
    let cancelled = false
    load()
      .then((table) => { if (!cancelled) setState({ table, status: 'done', error: null }) })
      .catch((err) => {
        if (cancelled) return
        setState({ table: null, status: 'error', error: String(err?.message || err) })
      })
    return () => { cancelled = true }
  }, [])

  return {
    ...state,
    reload: () => {
      resetUnitsTable()
      setState({ table: null, status: 'loading', error: null })
      load()
        .then((table) => setState({ table, status: 'done', error: null }))
        .catch((err) => setState({ table: null, status: 'error', error: String(err?.message || err) }))
    },
  }
}
